import type { ISerialize } from '@/objects/ISerialize';
import {
    deregisterSignalrCallback as DeregisterSignalrCallback,
    invokeServerSideMethod as InvokeServerSideMethod,
    registerSignalrCallback as RegisterSignalrCallback,
} from './signalr';
import type { IBaseObjStaticConstructor } from '@/objects/IBaseObjStaticConstructor';

/**
 * Represents a subscription to a signalr method
 *
 * This holds all the callbacks for one unique subscription so we can subscribe from several places in the frontend with the same parameters,
 * but only get one callback from the server
 *
 * This is unique per method + parameters combination
 *
 * We can however subscribe to the same method with different parameters, and that will create a new subscription
 */
export class SignalrSubscription<T extends ISerialize> {
    /**
     * The method to subscribe to
     */
    public readonly backendMethodName: string;

    /**
     * The parameters to the method
     */
    public readonly subscribeParameters: ISerialize;

    /**
     * This is the key the server generated when we subscribed.
     *
     * This is used to determine if the callback we get is actually ment for this subscription.
     *
     * The logic here is kinda hard to follow, but here goes:
     * We subscribe to a type/method, with some parameters.
     * The backend then generates a key from the method and parameters and saves the client subscription under that key.
     * When the server is going to do a callback it finds all the clients with a key that fits the parameteres of the data it wants to send
     * and invokes the frontend callback method.
     * The problem comes from the fact that the frontend callback method is invoked for ALL subscribers to that method, regardless of parameters.
     * So if we have subscribed to the type "MyMethod" with parameter A=1, THIS subscription instance will be for MyMethod-A=1.
     * If we also subscribe to "MyMethod" with parameter A=2, that will be in another subscription instance.
     * However, when the server is doing the callbacks, it will just call "MyMethod" in the frontend with whatever data it wants to notify for.
     * This will trigger both subscription instances since they are both listening to "MyMethod".
     * So we need a way to know if the supplied data is actually for this subscription instance when the server invokes the frontend method.
     *
     * So if we have more than one subscription to the same METHOD, all subscriptions will be invoked by the callback.
     * Only some of them should react to that specific invoke.
     *
     * Note that if we have only subscribed to A=1, then the server will not find the client for the A=2 case, and the client isnt notified at all. (which is what we want obviously)
     */
    private serverSideKey: string | null = null;

    /**
     * A list of callbacks to call when we receive data
     */
    private readonly callbacks: Set<(data: T[]) => void> = new Set();

    /**
     * If the subscription is allready invoked on the server
     */
    private isInvoked = false;

    /**
     * If we are in the process of invoking the server side method
     */
    private isInvoking = false;

    /**
     * Helper to generate the correct type from the backend data.
     */
    private type: IBaseObjStaticConstructor<T>;

    /**
     * Generates an unique key for the subscription based on the method and parameters
     *
     * This is unique in the sense that if two subscriptions have the same method and parameters, they will have the same key.
     *
     * This key is DIFFERENT from the server side subscription key, which is generated by the server and returned when we subscribe.
     *
     * Both keys however follow the same rules, the rules being that unique parameters create a unique key
     *
     * This is used to hold the subscriptions in a map on the frontend side.
     * @param method The method we will call in the backend to subscribe to something
     * @param subscribeParameters The parameters for the subscription
     * @returns A unique key for the subscription
     */
    public static GenerateFrontendSubscriptionKey(method: string, subscribeParameters: ISerialize): string {
        return `${method}-${subscribeParameters.serialize()}`;
    }

    /**
     * Constructor for the Subscription class
     * @param method the method to subscribe to
     * @param subscribeParameters the parameters to the method
     * @param callback the callback to call when we receive data
     * @param frontendKey A unique key for the subscription. Built from the parameters.
     */
    constructor(type: IBaseObjStaticConstructor<T>, method: string, subscribeParameters: ISerialize) {
        this.backendMethodName = method;
        this.subscribeParameters = subscribeParameters;
        this.type = type;
    }

    /**
     * Add a new callback to the subscription
     * @param callback
     */
    public async addCallback(callback: (data: T[]) => void) {
        this.callbacks.add(callback);

        if (this.isInvoking) {
            return;
        }

        // If we have not already invoked the server side method, do so now
        if (!this.isInvoked) {
            await this.startServerSubscription();
            await this.registerSignalrCallback();
        }
    }

    /**
     * Remove a callback from the subscription
     * @param callback
     */
    public async removeCallback(callback: (data: T[]) => void) {
        this.callbacks.delete(callback);

        // We need to unsubscribe from the server if we dont have any callbacks left
        if (this.callbacks.size === 0) {
            // Remove the subscription from the server
            await this.deregisterSignalrCallback();
            await this.stopServerSubscription();
        }
    }

    /**
     * Reregister with the server
     */
    public async resubscribe() {
        console.log('Resubscribing to', this.backendMethodName, this.subscribeParameters);

        // First we make sure we are not subscribed
        await this.stopServerSubscription();

        // This will restart the subscription on the server
        await this.startServerSubscription();
    }

    /**
     * Handling the callback from the backend
     * @param args
     */
    private backendCallback(data: T[]) {
        for (const callback of this.callbacks) {
            callback(data);
        }
    }

    /**
     * Start the subscription on the server
     */
    private async startServerSubscription() {
        if (this.isInvoked) {
            return;
        }

        this.isInvoking = true;

        try {
            // Note, we need to serialize to parse the enums, but InvokeServerSideMethod is expecting an object, so we end up with this ugly thing
            // TODO: Maybe make a method in the subscribeParameters objects to do this without falling back to JSON.parse?
            const params = JSON.parse(this.subscribeParameters.serialize());
            this.serverSideKey = await InvokeServerSideMethod<string>('Subscribe' + this.backendMethodName, params);
            this.isInvoked = true;
            this.isInvoking = false;
        } catch (e) {
            console.error('Could not connect to signalr: ', e);
            this.isInvoking = false;
        }
    }

    /**
     * Stop the subscription on the server
     */
    private async stopServerSubscription() {
        if (!this.isInvoked) {
            return;
        }

        try {
            // Note, we need to serialize to parse the enums, but InvokeServerSideMethod is expecting an object, so we end up with this ugly thing
            // TODO: Maybe make a method in the subscribeParameters objects to do this without falling back to JSON.parse?
            const params = JSON.parse(this.subscribeParameters.serialize());
            await InvokeServerSideMethod<void>('Unsubscribe' + this.backendMethodName, params);
            this.isInvoked = false;
        } catch (e) {
            console.error('Could not connect to signalr: ', e);
        }
    }

    /**
     * We need an internal callback to handle the data from the signalr connection. This will just convert the json to the correct type and call the backend callback
     * @param serverCallbackKey The key of the subscription the server is invoking the callback for
     * @param args The actual data arguments
     * @returns
     */
    private signalrCallbackWrapper = (serverCallbackKey: string, args: any[]) => {
        // Skip callbacks for the wrong subscription
        if (serverCallbackKey !== this.serverSideKey) {
            return;
        }
        this.backendCallback(args.map((x: any) => this.type._FromServerJson(x)));
    };

    /**
     * We register the internal callback with the signalr connection
     */
    private async registerSignalrCallback() {
        if (this.backendMethodName != 'DbDataChangedNotification') {
            // This is only to remove flooding of the console, since everything register this, and it is an pointless logging
            console.log('Registering signalr callback for', this.backendMethodName);
        }

        try {
            const self = this;
            await RegisterSignalrCallback(this.backendMethodName, self.signalrCallbackWrapper);
        } catch (e) {
            console.error('Could not register signalr callback: ', e);
        }
    }

    /**
     * We deregister the internal callback from the signalr connection
     */
    private async deregisterSignalrCallback() {
        console.log('Deregistering signalr callback for', this.backendMethodName);

        try {
            await DeregisterSignalrCallback(this.backendMethodName, this.signalrCallbackWrapper);
        } catch (e) {
            console.error('Could not deregister signalr callback: ', e);
        }
    }
}
